import type { CabinetBranchAccess, CabinetContext } from "@planerius/contracts/cabinet-auth"
import { useSyncExternalStore } from "react"

import { readPreferredBranch, writePreferredBranch } from "../lib/preferences"

/**
 * Выбор филиала.
 *
 * Сервер сейчас всегда отдаёт defaultBranchId = null, поэтому последний выбор помнит
 * устройство. Порядок источников: адрес → память устройства → первый действующий филиал.
 * Адрес выше памяти намеренно: ссылка на день конкретного филиала, присланная в чате,
 * обязана открыть именно его, а не тот, где человек был вчера.
 *
 * «Все филиалы» есть не везде. В журнале режима «все точки сразу» нет и быть не может:
 * у каждого филиала своя зона, свой шаг сетки и свои часы работы, и 10:00 одной колонки
 * и 10:00 соседней — разное абсолютное время. В сводке, отчётах и поиске клиента режим,
 * наоборот, основной, поэтому разрешение спрашивается у раздела (NavSection.allowsAllBranches),
 * а не зашито здесь.
 */

/** Значение «все филиалы» в адресе. Совпадает с тем, что пишется в память устройства. */
export const ALL_BRANCHES = "all"

/**
 * С какого числа филиалов в переключателе появляется поиск
 * (tokens.json → branchSwitcher.searchFrom).
 *
 * ПОЧЕМУ ЧИСЛО ЗДЕСЬ, А НЕ В КОМПОНЕНТЕ. ТЗ §4.4 говорило «если филиалов больше десятка»,
 * макет рисовал поиск с пяти, и дизайнер подтвердил пять осознанно. Пока число жило
 * внутри вёрстки, такое расхождение приходилось искать глазами по разметке; отсюда
 * на него можно сослаться и его можно проверить.
 *
 * Ниже пяти поиск не появляется, и это не экономия: поле поиска над списком из трёх
 * строк не сокращает ни одного движения — человек всё равно видит все филиалы сразу, —
 * зато занимает строку и предлагает набрать текст там, где достаточно ткнуть пальцем.
 */
export const BRANCH_SEARCH_FROM = 5

export type BranchSelection =
  | { readonly kind: "all" }
  | { readonly kind: "one"; readonly branch: CabinetBranchAccess }
  /** Человеку не открыт ни один филиал. Не ошибка интерфейса — состояние доступа. */
  | { readonly kind: "none" }

/**
 * ЧУЖОЙ ФИЛИАЛ В АДРЕСЕ — ЭТО ОТВЕТ, А НЕ МОЛЧАЛИВАЯ ПОДМЕНА (1-Э1.11).
 *
 * `?branch=<id>` приходит по ссылке от коллеги. Если этот филиал человеку не открыт,
 * resolveBranch молча подставляет другой — и человек смотрит на чужой день, уверенный,
 * что видит тот, который ему прислали: он ищет запись, не находит и звонит в поддержку.
 * Поэтому экран сначала спрашивает эту функцию и объясняет подмену словами.
 *
 * null — подменять нечего: филиал не назван или назван правильно.
 */
export function branchSubstitution(
  context: CabinetContext,
  wantedBranchId: string | null,
): { readonly wantedId: string; readonly openedName: string } | null {
  if (wantedBranchId === null) return null
  if (context.branches.some((branch) => branch.id === wantedBranchId)) return null
  const opened = resolveBranch(context, null)
  if (opened === null) return null
  return { wantedId: wantedBranchId, openedName: opened.name }
}

export function resolveBranch(
  context: CabinetContext,
  wantedBranchId: string | null,
): CabinetBranchAccess | null {
  const branches = context.branches
  if (branches.length === 0) return null

  const byId = (id: string | null): CabinetBranchAccess | undefined =>
    id === null ? undefined : branches.find((branch) => branch.id === id)

  const fromUrl = byId(wantedBranchId)
  if (fromUrl !== undefined) return fromUrl

  const stored = byId(readPreferredBranch(context.tenant.id))
  if (stored !== undefined) return stored

  return branches.find((branch) => branch.isActive) ?? branches[0] ?? null
}

/**
 * Выбор с учётом режима «все филиалы».
 *
 * «Все» берётся ТОЛЬКО когда раздел его допускает. Поэтому переход из отчётов, где стояло
 * «все филиалы», в журнал, где их нет, не открывает пустой экран и не показывает ошибку:
 * журнал молча берёт тот филиал, который человек выбирал последним поимённо.
 */
export function resolveSelection(
  context: CabinetContext,
  wantedBranchId: string | null,
  allowsAll: boolean,
): BranchSelection {
  if (context.branches.length === 0) return { kind: "none" }

  if (allowsAll) {
    const stored = readPreferredBranch(context.tenant.id)
    if (wantedBranchId === ALL_BRANCHES || (wantedBranchId === null && stored === ALL_BRANCHES)) {
      return { kind: "all" }
    }
  }

  const branch = resolveBranch(context, wantedBranchId)
  return branch === null ? { kind: "none" } : { kind: "one", branch }
}

/**
 * Хранилище выбранного филиала на устройстве.
 *
 * ПОЧЕМУ ХРАНИЛИЩЕ, А НЕ ПРОСТО ЗАПИСЬ В localStorage. Выбор филиала меняет шапку, полосу
 * пояса и содержимое экрана одновременно, а живёт он в двух местах сразу: в адресе (там,
 * где раздел его принимает) и в памяти устройства (везде). Пока это была одна запись
 * в localStorage, оболочка о ней не узнавала и показывала прежний филиал до перезагрузки
 * страницы. Версия-счётчик — минимальный способ сказать React, что память изменилась.
 */
let version = 0
const listeners = new Set<() => void>()

/*
 * ДРУГАЯ ВКЛАДКА ТОЖЕ МЕНЯЕТ ПАМЯТЬ. rememberBranch будит подписчиков только своей вкладки,
 * а филиал, выбранный в соседней, доезжал сюда лишь с перерисовкой оболочки (раз в минуту),
 * и то не до экранов за Outlet: шапка и колокольчик говорили о «Б», а лента — о «А»
 * (разбор w2but9x36, находка 21). Событие `storage` браузер шлёт во все прочие вкладки
 * того же сайта — по нему подписчики просыпаются все вместе.
 */
function onStorage(event: StorageEvent): void {
  if (event.key !== null && !event.key.startsWith("plnr.cabinet.branch.")) return
  version += 1
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0 && typeof window !== "undefined") {
    window.addEventListener("storage", onStorage)
  }
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage)
    }
  }
}

function getVersion(): number {
  return version
}

export function rememberBranch(context: CabinetContext, branchId: string): void {
  if (readPreferredBranch(context.tenant.id) === branchId) return
  writePreferredBranch(context.tenant.id, branchId)
  version += 1
  for (const listener of listeners) listener()
}

/** Подписка на память устройства: возвращает версию, чтобы React перерисовал оболочку. */
export function useBranchMemory(): number {
  return useSyncExternalStore(subscribe, getVersion, getVersion)
}
